import {
  denormalizeBoundingBox,
  isLineProtectedByRegion,
  isWordProtectedByRegion,
} from './localizationProtectedRegions';
import { buildTranslationSpanId } from './localizationTranslationSpanId';
import {
  type LocalizationBoundingBox,
  type LocalizationElement,
  type LocalizationImageDimensions,
  type LocalizationProtectedRegionGeometry,
  type LocalizationWord,
} from './localizationTypes';

export type LocalizationElementCoverage = 'full' | 'none' | 'partial';

export type LocalizationExcludedWordState = {
  index: number;
  /** False when some area covering this word also covers another element — deleting it here would silently free that other line too, so Edit has to go inert and point at the image instead. */
  isEditable: boolean;
  kind: 'excluded';
  /** Stable across the review entry, its inert canvas box, and the hover signal that lights both. */
  protectedEntryKey: string;
  /** Every area covering this word — undo removes all of them, since leaving one behind would not free the word. */
  protectedRegionIds: string[];
  word: LocalizationWord;
};

export type LocalizationWordState =
  | LocalizationExcludedWordState
  | { index: number; kind: 'translatable'; word: LocalizationWord };

export type LocalizationTranslatableSegment = {
  boundingBox: LocalizationBoundingBox;
  id: string;
  startWordIndex: number;
  text: string;
  wordCount: number;
};

export type LocalizationElementAnalysis = {
  coverage: LocalizationElementCoverage;
  element: LocalizationElement;
  /** Meaningful only when `coverage === 'full'` or the element has no word boxes — false when some area behind `protectedRegionIds` also covers another element. */
  isEditable: boolean;
  /** `coverage !== 'full'`, kept as its own field only so callers don't recompute it — the constructor guarantees the two never diverge. */
  isTranslatable: boolean;
  /** Every area excluding any part of this element — what its Protected entry removes to restore the whole row. */
  protectedRegionIds: string[];
  /** Empty whenever `coverage` is `'full'`. */
  segments: LocalizationTranslatableSegment[];
  /** Index-aligned with `element.words`. */
  wordStates: LocalizationWordState[];
};

export const buildProtectedEntryKey = (elementId: string, wordIndex: number | null): string =>
  wordIndex === null ? elementId : `${elementId}:word:${wordIndex}`;

export const unionBoundingBox = (boxes: LocalizationBoundingBox[]): LocalizationBoundingBox => {
  const minX = Math.min(...boxes.map((box) => box.x));
  const minY = Math.min(...boxes.map((box) => box.y));
  const maxX = Math.max(...boxes.map((box) => box.x + box.width));
  const maxY = Math.max(...boxes.map((box) => box.y + box.height));

  return { height: maxY - minY, width: maxX - minX, x: minX, y: minY };
};

const buildSegment = (
  elementId: string,
  words: LocalizationWord[],
  start: number,
  end: number,
): LocalizationTranslatableSegment => {
  const runWords = words.slice(start, end);
  const text = runWords.map((word) => word.text).join(' ');

  return {
    boundingBox: unionBoundingBox(runWords.map((word) => word.boundingBox)),
    id: buildTranslationSpanId(elementId, start, text),
    startWordIndex: start,
    text,
    wordCount: runWords.length,
  };
};

const buildTranslatableSegments = (
  elementId: string,
  words: LocalizationWord[],
  wordStates: LocalizationWordState[],
): LocalizationTranslatableSegment[] => {
  const segments: LocalizationTranslatableSegment[] = [];
  let runStart = -1;

  wordStates.forEach((state, index) => {
    if (state.kind === 'excluded') {
      if (runStart !== -1) {
        segments.push(buildSegment(elementId, words, runStart, index));
        runStart = -1;
      }

      return;
    }

    if (runStart === -1) {
      runStart = index;
    }
  });

  if (runStart !== -1) {
    segments.push(buildSegment(elementId, words, runStart, words.length));
  }

  return segments;
};

/** Keyed on OCR's `originalText`, not `words.join(' ')`, since the two can differ in normalization and the span id must stay stable while the line is unsegmented. */
export const wholeLineSegment = (element: LocalizationElement): LocalizationTranslatableSegment => ({
  boundingBox: element.boundingBox,
  id: buildTranslationSpanId(element.id, 0, element.originalText),
  startWordIndex: 0,
  text: element.originalText,
  wordCount: element.words.length,
});

/** Folds in the segment's own word boxes because a whole-line segment carries OCR's line box, which isn't guaranteed to enclose every word box it reported. */
export const buildSkipAreaBoundingBox = (
  element: LocalizationElement,
  segment: LocalizationTranslatableSegment,
): LocalizationBoundingBox => {
  const segmentWords = element.words.slice(segment.startWordIndex, segment.startWordIndex + segment.wordCount);

  return unionBoundingBox([segment.boundingBox, ...segmentWords.map((word) => word.boundingBox)]);
};

const findCoveringRegionIds = (word: LocalizationWord, regions: LocalizationProtectedRegionGeometry[]): string[] =>
  regions.filter((region) => isWordProtectedByRegion(word.boundingBox, region.boundingBox)).map((region) => region.id);

const analyzeElement = (
  element: LocalizationElement,
  regions: LocalizationProtectedRegionGeometry[],
  coveringRegionIdsByWord: string[][],
  isRegionConfinedToElement: (regionId: string) => boolean,
): LocalizationElementAnalysis => {
  if (element.words.length === 0) {
    const protectedRegionIds = regions
      .filter((region) => isLineProtectedByRegion(element.boundingBox, region.boundingBox))
      .map((region) => region.id);
    const coverage: LocalizationElementCoverage = protectedRegionIds.length > 0 ? 'full' : 'none';

    return {
      coverage,
      element,
      isEditable: protectedRegionIds.every(isRegionConfinedToElement),
      isTranslatable: coverage !== 'full',
      protectedRegionIds,
      segments: coverage === 'full' ? [] : [wholeLineSegment(element)],
      wordStates: [],
    };
  }

  const wordStates = element.words.map((word, index): LocalizationWordState => {
    const protectedRegionIds = coveringRegionIdsByWord[index] ?? [];

    if (protectedRegionIds.length === 0) {
      return { index, kind: 'translatable', word };
    }

    return {
      index,
      isEditable: protectedRegionIds.every(isRegionConfinedToElement),
      kind: 'excluded',
      protectedEntryKey: buildProtectedEntryKey(element.id, index),
      protectedRegionIds,
      word,
    };
  });
  const excludedStates = wordStates.filter(
    (state): state is LocalizationExcludedWordState => state.kind === 'excluded',
  );
  let coverage: LocalizationElementCoverage;

  if (excludedStates.length === element.words.length) {
    coverage = 'full';
  } else if (excludedStates.length === 0) {
    coverage = 'none';
  } else {
    coverage = 'partial';
  }

  let segments: LocalizationTranslatableSegment[];

  if (coverage === 'full') {
    segments = [];
  } else if (coverage === 'none') {
    segments = [wholeLineSegment(element)];
  } else {
    segments = buildTranslatableSegments(element.id, element.words, wordStates);
  }

  const protectedRegionIds = Array.from(new Set(excludedStates.flatMap((state) => state.protectedRegionIds)));

  return {
    coverage,
    element,
    isEditable: protectedRegionIds.every(isRegionConfinedToElement),
    isTranslatable: coverage !== 'full',
    protectedRegionIds,
    segments,
    wordStates,
  };
};

/** Every downstream projection reads only this output, never the raw areas again, so coverage can't be decided a second, inconsistent way. */
export const analyzeLocalizationElements = (
  elements: LocalizationElement[],
  protectedRegions: LocalizationProtectedRegionGeometry[],
  imageDimensions: LocalizationImageDimensions | null,
): LocalizationElementAnalysis[] => {
  const regions: LocalizationProtectedRegionGeometry[] =
    protectedRegions.length === 0 || !imageDimensions
      ? []
      : protectedRegions.map((region) => ({
          boundingBox: denormalizeBoundingBox(region.boundingBox, imageDimensions),
          id: region.id,
        }));

  // One geometry pass per word, shared by every later step — confinement below is bookkeeping over its output, never a second pass.
  const coveringRegionIdsByWordByElement = elements.map((element) =>
    element.words.map((word) => findCoveringRegionIds(word, regions)),
  );
  const coveringRegionIdsByElement = elements.map((element, elementIndex) =>
    element.words.length === 0
      ? regions
          .filter((region) => isLineProtectedByRegion(element.boundingBox, region.boundingBox))
          .map((region) => region.id)
      : Array.from(new Set(coveringRegionIdsByWordByElement[elementIndex]?.flat() ?? [])),
  );

  // Which elements each area touches at all, gathered once so confinement below is bookkeeping over this output rather than a
  // second geometry pass over every word and line box.
  const elementIdsByRegionId = new Map<string, Set<string>>();

  elements.forEach((element, elementIndex) => {
    coveringRegionIdsByElement[elementIndex]?.forEach((regionId) => {
      const elementIds = elementIdsByRegionId.get(regionId) ?? new Set<string>();

      elementIds.add(element.id);
      elementIdsByRegionId.set(regionId, elementIds);
    });
  });

  return elements.map((element, elementIndex) => {
    // An area confined to this element touches no other line — deleting it can never silently release text on a row the user isn't looking at.
    const isRegionConfinedToElement = (regionId: string): boolean => {
      const elementIds = elementIdsByRegionId.get(regionId);

      return elementIds !== undefined && elementIds.size === 1 && elementIds.has(element.id);
    };

    return analyzeElement(
      element,
      regions,
      coveringRegionIdsByWordByElement[elementIndex] ?? [],
      isRegionConfinedToElement,
    );
  });
};

/** Takes ids rather than the full region so this stays blind to why an id is in the set (e.g. drawn vs. skip) — callers that care about provenance filter the ids before calling in. */
export const hasContentExcludedByRegionIds = (
  analyses: LocalizationElementAnalysis[],
  regionIds: ReadonlySet<string>,
): boolean => analyses.some((analysis) => analysis.protectedRegionIds.some((id) => regionIds.has(id)));
